import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Button, DatePicker, Form, Input, InputNumber, Radio, Select, Typography, Upload, message
} from 'antd';
import { DeleteOutlined, EyeOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import AttachmentPreviewModal from '../AttachmentPreviewModal';
import './ExpenseClaimForm.css';

const { Text } = Typography;
const UPLOAD_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.bmp';

function FormSection({ title, children }) {
  return (
    <section className="expense-claim-section">
      <div className="expense-claim-section-title">{title}</div>
      {children}
    </section>
  );
}

function roundMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.round(num * 100) / 100;
}

function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0.00';
  return num.toFixed(2);
}

function computeLineDiff(payable, paid) {
  if (payable == null || paid == null || payable === '' || paid === '') return null;
  return roundMoney(Number(payable) - Number(paid));
}

function computeTotals(lines = []) {
  return (lines || []).reduce((acc, line) => ({
    total_headcount: acc.total_headcount + Number(line?.headcount || 0),
    total_payable_amount: roundMoney(acc.total_payable_amount + Number(line?.payable_amount || 0)),
    total_paid_amount: roundMoney(acc.total_paid_amount + Number(line?.paid_amount || 0)),
    total_diff_amount: roundMoney(acc.total_diff_amount + Number(line?.diff_amount || 0))
  }), {
    total_headcount: 0,
    total_payable_amount: 0,
    total_paid_amount: 0,
    total_diff_amount: 0
  });
}

function normalizeLinePeriod(period) {
  if (!period) return undefined;
  if (dayjs.isDayjs(period)) return period.format('YYYY-MM');
  return String(period).slice(0, 7);
}

function normalizeMoneyInput(value) {
  if (value == null || value === '') return value;
  return roundMoney(Number(value));
}

function parseMoneyInput(value) {
  if (value == null || value === '') return value;
  const cleaned = String(value).replace(/[^\d.]/g, '');
  const [intPart, decPart = ''] = cleaned.split('.');
  const normalized = decPart ? `${intPart}.${decPart.slice(0, 2)}` : intPart;
  if (!normalized || normalized === '.') return undefined;
  return Number(normalized);
}

const MONEY_INPUT_PROPS = {
  size: 'small',
  min: 0,
  precision: 2,
  step: 0.01,
  placeholder: '0.00',
  parser: parseMoneyInput
};

function createEmptyLine() {
  return {
    headcount: undefined,
    payable_amount: undefined,
    paid_amount: undefined,
    diff_amount: undefined,
    diff_remark: undefined
  };
}

export function getSocialFundPaymentInitialValues(defaultPeriod = dayjs().format('YYYY-MM')) {
  return {
    period: defaultPeriod,
    payment_lines: [createEmptyLine()],
    attachments: []
  };
}

export async function buildSocialFundPaymentPayload(form, submit) {
  const values = submit ? await form.validateFields() : form.getFieldsValue(true);
  const period = normalizeLinePeriod(values.period);
  const lines = (values.payment_lines || []).map((line) => ({
    company_id: line.company_id,
    period,
    headcount: line.headcount,
    payable_amount: line.payable_amount,
    paid_amount: line.paid_amount,
    diff_amount: computeLineDiff(line.payable_amount, line.paid_amount),
    diff_remark: line.diff_remark || undefined
  }));

  if (submit) {
    if (!period) throw new Error('请选择费用期间');
    const companySet = new Set();
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      if (!line.company_id) throw new Error(`第 ${i + 1} 行请选择公司主体`);
      if (companySet.has(line.company_id)) throw new Error('同一表单中公司主体不可重复');
      companySet.add(line.company_id);
      if (line.paid_amount != null && line.payable_amount != null && line.paid_amount > line.payable_amount) {
        throw new Error(`第 ${i + 1} 行实缴费额不能大于应缴费额`);
      }
    }
    if (!lines.length) throw new Error('请至少添加一行缴费明细');
  }

  return {
    submit,
    payment_type: values.payment_type,
    period,
    payment_lines: lines,
    attachments: values.attachments || [],
    remark: values.remark || undefined
  };
}

export default function SocialFundPaymentForm({ form, initialValues, onValuesChange }) {
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [defaultPeriod, setDefaultPeriod] = useState(dayjs().format('YYYY-MM'));

  const paymentLines = Form.useWatch('payment_lines', form) || [];
  const attachments = Form.useWatch('attachments', form) || [];
  const totals = useMemo(() => computeTotals(paymentLines), [paymentLines]);

  const companySelectOptions = useMemo(
    () => companyOptions.map((item) => ({ value: item.id, label: item.label || item.company_name })),
    [companyOptions]
  );

  const loadMeta = useCallback(async () => {
    try {
      setLoadingMeta(true);
      const res = await api.get('/expense/fin-social-fund-payment/meta');
      if (!res.data.success) return;
      const data = res.data.data || {};
      setPaymentTypeOptions(data.payment_type_options || []);
      setCompanyOptions(data.company_options || []);
      if (data.default_period) setDefaultPeriod(data.default_period);
    } catch (e) {
      message.error(e.response?.data?.message || '加载表单失败');
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  // 退回重提场景：先用已有 form_data 回填，再让下面的默认值逻辑在已有值时跳过
  useLayoutEffect(() => {
    if (!initialValues || !Object.keys(initialValues).length) return;
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  useLayoutEffect(() => {
    const lines = form.getFieldValue('payment_lines');
    const currentPeriod = form.getFieldValue('period');
    if (!lines?.length) {
      form.setFieldsValue(getSocialFundPaymentInitialValues(defaultPeriod));
    } else if (!currentPeriod) {
      form.setFieldsValue({ period: defaultPeriod });
    }
  }, [defaultPeriod, form]);

  const syncLineDiff = (index) => {
    const lines = [...(form.getFieldValue('payment_lines') || [])];
    const line = lines[index];
    if (!line) return;
    lines[index] = {
      ...line,
      diff_amount: computeLineDiff(line.payable_amount, line.paid_amount)
    };
    form.setFieldsValue({ payment_lines: lines });
  };

  const uploadAttachment = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post('/expense/fin-social-fund-payment/upload-attachment', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (!res.data.success) throw new Error(res.data.message || '上传失败');
    return res.data.data;
  };

  const handleAttachmentUpload = async (file) => {
    try {
      setUploadingAttachment(true);
      const data = await uploadAttachment(file);
      const uploaded = {
        file_name: data.file_name || file.name,
        file_path: data.file_path
      };
      form.setFieldsValue({
        attachments: [...(form.getFieldValue('attachments') || []), uploaded]
      });
      message.success('附件已上传');
    } catch (e) {
      message.error(e.response?.data?.message || e.message || '上传失败');
    } finally {
      setUploadingAttachment(false);
    }
    return Upload.LIST_IGNORE;
  };

  const handleAttachmentRemove = (index) => {
    form.setFieldsValue({
      attachments: attachments.filter((_, i) => i !== index)
    });
  };

  const openAttachmentPreview = (file) => {
    if (!file?.file_path) return;
    setPreviewItem({
      path: file.file_path,
      name: file.file_name || '附件'
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="expense-claim-form social-fund-payment-form"
      onValuesChange={onValuesChange}
    >
      <div className="social-fund-header-row">
        <Form.Item
          className="social-fund-type-item"
          name="payment_type"
          label="类型"
          rules={[{ required: true, message: '请选择类型' }]}
        >
          <Radio.Group options={paymentTypeOptions} disabled={loadingMeta} />
        </Form.Item>
        <Form.Item
          className="social-fund-period-item"
          name="period"
          label="费用期间"
          rules={[{ required: true, message: '请选择费用期间' }]}
          getValueProps={(value) => ({
            value: value ? dayjs(`${normalizeLinePeriod(value)}-01`) : undefined
          })}
          normalize={(value) => (value ? value.format('YYYY-MM') : undefined)}
        >
          <DatePicker picker="month" format="YYYY-MM" allowClear={false} />
        </Form.Item>
      </div>

      <FormSection title="缴费明细">
        <Form.List name="payment_lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <div key={field.key} className="social-fund-line-row">
                  <Form.Item
                    {...field}
                    className="social-fund-line-field social-fund-line-field--company"
                    name={[field.name, 'company_id']}
                    label="公司主体"
                    rules={[{ required: true, message: '请选择公司主体' }]}
                  >
                    <Select
                      size="small"
                      showSearch
                      optionFilterProp="label"
                      placeholder="请选择"
                      options={companySelectOptions}
                      loading={loadingMeta}
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    className="social-fund-line-field social-fund-line-field--compact"
                    name={[field.name, 'headcount']}
                    label="缴费人数"
                    rules={[{ required: true, message: '请填写缴费人数' }]}
                  >
                    <InputNumber size="small" min={1} precision={0} placeholder="人数" />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    className="social-fund-line-field social-fund-line-field--amount"
                    name={[field.name, 'payable_amount']}
                    label="应缴费额"
                    normalize={normalizeMoneyInput}
                    rules={[{ required: true, message: '请填写应缴费额' }]}
                  >
                    <InputNumber
                      {...MONEY_INPUT_PROPS}
                      onChange={() => syncLineDiff(index)}
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    className="social-fund-line-field social-fund-line-field--amount"
                    name={[field.name, 'paid_amount']}
                    label="实缴费额"
                    normalize={normalizeMoneyInput}
                    rules={[
                      { required: true, message: '请填写实缴费额' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const payable = getFieldValue(['payment_lines', index, 'payable_amount']);
                          if (value == null || payable == null || Number(value) <= Number(payable)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('不能大于应缴费额'));
                        }
                      })
                    ]}
                  >
                    <InputNumber
                      {...MONEY_INPUT_PROPS}
                      onChange={() => syncLineDiff(index)}
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    className="social-fund-line-field social-fund-line-field--amount"
                    name={[field.name, 'diff_amount']}
                    label="差额"
                  >
                    <InputNumber size="small" precision={2} disabled placeholder="—" />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    className="social-fund-line-field social-fund-line-field--remark"
                    name={[field.name, 'diff_remark']}
                    label="差额备注"
                  >
                    <Input size="small" placeholder="选填" maxLength={200} />
                  </Form.Item>
                  {fields.length > 1 && (
                    <div className="social-fund-line-remove">
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    </div>
                  )}
                </div>
              ))}
              <Button
                type="dashed"
                size="small"
                className="social-fund-add-line-btn"
                icon={<PlusOutlined />}
                onClick={() => add(createEmptyLine())}
              >
                新增缴费明细
              </Button>
            </>
          )}
        </Form.List>

        <div className="expense-claim-summary social-fund-summary-block">
          <div className="expense-claim-summary-item">
            <Text type="secondary">缴费人数合计</Text>
            <span className="expense-claim-summary-total">{totals.total_headcount}</span>
          </div>
          <div className="expense-claim-summary-item">
            <Text type="secondary">应缴费额合计</Text>
            <span className="expense-claim-summary-total">{formatMoney(totals.total_payable_amount)}</span>
          </div>
          <div className="expense-claim-summary-item">
            <Text type="secondary">实缴金额合计</Text>
            <span className="expense-claim-summary-total">{formatMoney(totals.total_paid_amount)}</span>
          </div>
          <div className="expense-claim-summary-item">
            <Text type="secondary">抵扣额/差额合计</Text>
            <span className="expense-claim-summary-total">{formatMoney(totals.total_diff_amount)}</span>
          </div>
        </div>
      </FormSection>

      <Form.Item className="social-fund-attachment-item" label="附件">
        <Upload
          accept={UPLOAD_ACCEPT}
          beforeUpload={handleAttachmentUpload}
          showUploadList={false}
          disabled={uploadingAttachment}
        >
          <Button size="small" icon={<UploadOutlined />} loading={uploadingAttachment}>上传附件</Button>
        </Upload>
        {attachments.length > 0 && (
          <div className="expense-line-attachment social-fund-attachment-list">
            {attachments.map((file, index) => (
              <div key={`${file.file_path}-${index}`} className="expense-line-attachment-file">
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  aria-label="预览附件"
                  className="expense-line-attachment-eye"
                  onClick={() => openAttachmentPreview(file)}
                />
                <Text ellipsis={{ tooltip: file.file_name || '附件' }} className="expense-line-attachment-name">
                  {file.file_name || '附件'}
                </Text>
                <Button
                  type="link"
                  size="small"
                  danger
                  className="expense-line-attachment-remove"
                  onClick={() => handleAttachmentRemove(index)}
                >
                  删除
                </Button>
              </div>
            ))}
          </div>
        )}
        <Form.Item name="attachments" hidden initialValue={[]}><Input hidden /></Form.Item>
      </Form.Item>

      <AttachmentPreviewModal
        item={previewItem}
        open={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
      />
    </Form>
  );
}
